import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDate, PROJECT_STATUS, MILESTONES, RISK } from "@/lib/constants";
import { RiskDot } from "@/components/RiskDot";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ProjectToolsPanel } from "@/components/ProjectToolsPanel";
import { ProjectPositionsPanel } from "@/components/ProjectPositionsPanel";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, CheckCircle, Plus, Trash, FileText, Printer, ArrowsClockwise, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import TasksPanel from "@/components/TasksPanel";
import PlanningPanel from "@/components/PlanningPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import ProductionPanel from "@/components/ProductionPanel";
import FieldPanel from "@/components/FieldPanel";
import WarrantyPanel from "@/components/WarrantyPanel";
import ProjectFolderPanel from "@/components/ProjectFolderPanel";
import ContractFisaDialog from "@/components/ContractFisaDialog";
import ProjectFlowComms from "@/components/ProjectFlowComms";
import MontajTeamsPanel from "@/components/MontajTeamsPanel";
import ReperePanel from "@/components/ReperePanel";
import MontajFinalizare from "@/components/MontajFinalizare";
import ProjectMemory from "@/components/ProjectMemory";
import GateDocsPanel from "@/components/GateDocsPanel";
import WorkflowPanel from "@/components/WorkflowPanel";
import WorkflowStageBar from "@/components/WorkflowStageBar";

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm">{value || "—"}</div>
    </div>
  );
}

function MilestoneTracker({ current, onSet, canEdit }) {
  const idx = MILESTONES.findIndex((m) => m.code === current);
  return (
    <div className="overflow-x-auto pb-2" data-testid="milestone-tracker">
      <div className="flex min-w-max items-start gap-1">
        {MILESTONES.map((m, i) => {
          const state = i < idx ? "done" : i === idx ? "current" : "todo";
          return (
            <div key={m.code} className="flex items-center">
              <button
                disabled={!canEdit}
                onClick={() => canEdit && onSet(m.code)}
                className="flex w-24 flex-col items-center gap-1.5 text-center"
                data-testid={`milestone-${m.code}`}
              >
                <span className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-full border-2 text-xs font-bold transition-colors",
                  state === "done" && "border-emerald-500 bg-emerald-500/20 text-emerald-400",
                  state === "current" && "border-primary bg-primary text-white",
                  state === "todo" && "border-border bg-white/5 text-muted-foreground"
                )}>
                  {state === "done" ? <CheckCircle size={16} weight="fill" /> : m.code}
                </span>
                <span className={cn("text-[10px] leading-tight", state === "current" ? "text-foreground" : "text-muted-foreground")}>
                  {m.label}
                </span>
              </button>
              {i < MILESTONES.length - 1 && (
                <div className={cn("h-0.5 w-4 shrink-0", i < idx ? "bg-emerald-500" : "bg-border")} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasRole, user } = useAuth();
  const [tab, setTab] = useState(searchParams.get("tab") || "general");
  const [folderHint, setFolderHint] = useState(null);
  const [wfHint, setWfHint] = useState(null);
  const jumpTo = (t, step) => {
    setTab(t);
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (t === "dosar") setFolderHint({ doc: step?.doc, key: step?.key, nonce: Date.now() });
    else if (t === "workflow") setWfHint({ label: step?.label, actor: step?.actor, key: step?.key, nonce: Date.now() });
  };
  const [p, setP] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [risk, setRisk] = useState({ title: "", level: "galben", description: "" });
  const [doc, setDoc] = useState({ name: "", type: "" });

  const isMontaj = (user?.departments || []).includes("montaj");
  const canEdit = hasRole("pm", "inginer", "vanzari", "asistent_vanzari") || isMontaj;
  const canMontaj = hasRole("pm", "inginer") || isMontaj;

  const load = () => api.get(`/projects/${id}`).then((r) => setP(r.data)).catch(() => navigate("/projects"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const [momente, setMomente] = useState(null);
  const [fpSchema, setFpSchema] = useState([]);
  const [fpBusy, setFpBusy] = useState(false);
  useEffect(() => { api.get("/fp-schema").then((r) => setFpSchema(r.data)).catch(() => {}); }, []);
  const loadMomente = () => api.get(`/projects/${id}/momente`).then((r) => setMomente(r.data)).catch(() => {});
  useEffect(() => { loadMomente(); /* eslint-disable-next-line */ }, [id]);

  const setMoment = async (field, value) => {
    try {
      const { data } = await api.put(`/projects/${id}/momente`, { [field]: value });
      setMomente(data);
      load();
      toast.success("Moment actualizat — operațiunea din workflow a fost legată de această dată");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const runMontajReminders = async () => {
    try {
      const { data } = await api.post(`/montaj/reminders/run`);
      toast.success(`Remindere montaj trimise: ${data.reminded}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!p) return <div className="p-8 text-muted-foreground">Se încarcă…</div>;

  const patch = async (body, msg) => {
    try {
      const { data } = await api.put(`/projects/${id}`, body);
      setP(data);
      if (msg) toast.success(msg);
      return data;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = () => { setForm(JSON.parse(JSON.stringify(p))); setEditing(true); };
  const delProject = async () => {
    if (!window.confirm(`Ștergi proiectul „${p.code} — ${p.name}"?\n\nSe șterge TOT (documente, workflow, activități), dar proiectul ajunge în Coșul de recuperare (Jurnal de acces) de unde poate fi restaurat sau șters definitiv.`)) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Proiect mutat în Coșul de recuperare");
      navigate("/projects");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const finalizeProject = async () => {
    if (!window.confirm(`Finalizezi proiectul „${p.code} — ${p.name}"?\n\nProiectul (cu tot cu dosar digital) se mută în Arhiva de proiecte și nu mai apare în listele active.`)) return;
    try {
      await api.post(`/projects/${id}/finalize`);
      toast.success("Proiect finalizat și mutat în Arhivă");
      navigate("/arhiva");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unarchiveProject = async () => {
    try {
      await api.post(`/projects/${id}/unarchive`);
      toast.success("Proiect dezarhivat");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setFp = (k, v) => setForm((f) => ({ ...f, fp: { ...(f.fp || {}), [k]: v } }));
  const onFpExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFpBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/projects/${id}/fp-import-excel`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, fp: { ...(f.fp || {}), ...(data.fp || {}) } }));
      toast.success(`Fișa tehnică a fost completată din Excel (${data.count} câmpuri).`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setFpBusy(false); e.target.value = ""; }
  };
  const addRacord = () => setFp("racorduri", [...(form?.fp?.racorduri || []), { tip: "", nr_buc: "", dn: "" }]);
  const setRacord = (i, k, v) => { const list = [...(form?.fp?.racorduri || [])]; list[i] = { ...list[i], [k]: v }; setFp("racorduri", list); };
  const delRacord = (i) => setFp("racorduri", (form?.fp?.racorduri || []).filter((_, x) => x !== i));
  const saveEdit = async () => {
    await patch({
      code: form.code, name: form.name, client: form.client, beneficiary: form.beneficiary, country: form.country,
      site_address: form.site_address, product_type: form.product_type,
      contract_ref: form.contract_ref, order_ref: form.order_ref, offer_ref: form.offer_ref,
      contract_deadline: form.contract_deadline, internal_target: form.internal_target,
      current_estimate: form.current_estimate, delivery_terms: form.delivery_terms,
      data_livrare: form.data_livrare, data_montaj: form.data_montaj,
      payment_terms: form.payment_terms, tech: form.tech,
      fp: form.fp || {},
      contacts: form.contacts,
      budget: Number(form.budget) || 0, costs: Number(form.costs) || 0,
      status: form.status,
    }, "Fișă actualizată");
    setEditing(false);
  };

  const onContractExtracted = (ex) => {
    const base = JSON.parse(JSON.stringify(p));
    const merged = { ...base };
    ["client", "beneficiary", "country", "site_address", "product_type", "contract_ref",
     "order_ref", "offer_ref", "contract_deadline", "delivery_terms", "payment_terms",
     "data_livrare", "data_montaj"].forEach((k) => { if (ex[k]) merged[k] = ex[k]; });
    if (ex.budget) merged.budget = ex.budget;
    if (ex.fp && typeof ex.fp === "object") merged.fp = { ...(merged.fp || {}), ...ex.fp };
    if (Array.isArray(ex.contacts) && ex.contacts.length) merged.contacts = ex.contacts;
    setForm(merged);
    setEditing(true);
    load();
  };

  const addRisk = async () => {
    if (!risk.title.trim()) return;
    const risks = [...(p.risks || []), { ...risk, id: crypto.randomUUID(), created_at: new Date().toISOString() }];
    await patch({ risks }, "Risc adăugat");
    setRisk({ title: "", level: "galben", description: "" });
  };
  const delRisk = async (rid) => patch({ risks: (p.risks || []).filter((r) => r.id !== rid) });

  const addDoc = async () => {
    if (!doc.name.trim()) return;
    const documents = [...(p.documents || []), { ...doc, id: crypto.randomUUID(), revision: 1, created_at: new Date().toISOString() }];
    await patch({ documents }, "Document adăugat");
    setDoc({ name: "", type: "" });
  };
  const delDoc = async (did) => patch({ documents: (p.documents || []).filter((d) => d.id !== did) });

  const bumpRevision = async () => {    try {
      const { data } = await api.post(`/projects/${id}/tech-revision`);
      if (data.escalated) toast.warning(`Revizia ${data.tech_revision} — escaladat la Director Executiv (regula R02)`);
      else toast.success(`Revizie proiect tehnic: ${data.tech_revision}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const margin = p.budget ? Math.round(((p.budget - p.costs) / p.budget) * 100) : 0;

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="project-detail">
      <Link to="/projects" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Înapoi la proiecte
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
          {p.archived && <span className="ml-2 inline-block rounded bg-amber-500/15 px-2 py-0.5 text-[11px] font-bold text-amber-400" data-testid="archived-badge">ARHIVAT</span>}
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">{p.name}</h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
            <RiskDot level={p.risk_level} label size="lg" />
            <span>·</span>
            <span>{PROJECT_STATUS[p.status]}</span>
            <span>·</span>
            <span>{p.client}</span>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Select value={p.risk_level} onValueChange={(v) => patch({ risk_level: v }, "Nivel risc actualizat")}>
              <SelectTrigger className="w-36" data-testid="risk-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(RISK).map(([k, v]) => <SelectItem key={k} value={k}>Risc {v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {canEdit && <ContractFisaDialog projectId={id} onExtracted={onContractExtracted} />}
          {canEdit && <Button variant="outline" onClick={openEdit} data-testid="edit-project-btn">Editează fișa</Button>}
          {hasRole("admin", "pm") && !p.archived && <Button variant="secondary" onClick={finalizeProject} data-testid="finalize-project-btn"><Archive size={16} className="mr-1.5" /> Finalizează</Button>}
          {hasRole("admin") && p.archived && <Button variant="secondary" onClick={unarchiveProject} data-testid="unarchive-project-btn"><ArrowCounterClockwise size={16} className="mr-1.5" /> Dezarhivează</Button>}
          {hasRole("admin") && <Button variant="destructive" onClick={delProject} data-testid="delete-project-btn"><Trash size={16} className="mr-1.5" /> Șterge</Button>}
          <Button variant="outline" onClick={() => window.open(`/print/project/${id}`, "_blank")} data-testid="print-project-btn"><Printer size={16} className="mr-1.5" /> PDF</Button>
        </div>
      </div>

      <WorkflowStageBar projectId={id} onJump={jumpTo} />

      <ProjectFlowComms projectId={id} projectCode={p.code} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="tehnic">Tehnic</TabsTrigger>
          <TabsTrigger value="rezervoare" data-testid="tab-rezervoare">Rezervoare</TabsTrigger>
          <TabsTrigger value="termene">Termene</TabsTrigger>
          <TabsTrigger value="echipa">Echipă</TabsTrigger>
          {canMontaj && <TabsTrigger value="echipe_montaj" data-testid="tab-echipe-montaj">Echipe montaj</TabsTrigger>}
          <TabsTrigger value="buget">Buget</TabsTrigger>
          <TabsTrigger value="documente">Documente</TabsTrigger>
          <TabsTrigger value="dosar" data-testid="tab-dosar">Dosar</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="tab-workflow">Workflow</TabsTrigger>
          <TabsTrigger value="taskuri" data-testid="tab-taskuri">Task-uri</TabsTrigger>
          <TabsTrigger value="planificare" data-testid="tab-planificare">Planificare</TabsTrigger>
          <TabsTrigger value="productie" data-testid="tab-productie">Producție</TabsTrigger>
          <TabsTrigger value="repere" data-testid="tab-repere">Repere</TabsTrigger>
          <TabsTrigger value="scule" data-testid="tab-scule">Scule & Utilaje</TabsTrigger>
          <TabsTrigger value="memorie_ai" data-testid="tab-memorie-ai">Memorie AI</TabsTrigger>
          {canMontaj && <TabsTrigger value="finalizare_montaj" data-testid="tab-finalizare-montaj">Finalizare montaj</TabsTrigger>}
          <TabsTrigger value="teren" data-testid="tab-teren">Teren</TabsTrigger>
          <TabsTrigger value="garantie" data-testid="tab-garantie">Garanție</TabsTrigger>
          <TabsTrigger value="riscuri">Riscuri</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          {p.ai_project_summary?.summary && (
            <div className="mb-5 rounded-xl border border-primary/30 bg-primary/5 p-5" data-testid="general-memory-card">
              <div className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary">
                <i className="fa-solid fa-brain" /> Memorie proiect (AI)
              </div>
              <p className="text-sm leading-relaxed text-foreground/90">{p.ai_project_summary.summary}</p>
              <button onClick={() => setTab("memorie_ai")} className="mt-2 text-xs font-medium text-primary hover:underline" data-testid="general-memory-open">Vezi memoria completă și întreabă proiectul →</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-5 rounded-xl border border-border bg-card p-6 sm:grid-cols-3">
            <Field label="Client" value={p.client} />
            <Field label="Beneficiar final" value={p.beneficiary} />
            <Field label="Țară" value={p.country} />
            <Field label="Adresă șantier" value={p.site_address} />
            <Field label="Tip produs" value={p.product_type} />
            <Field label="Contract" value={p.contract_ref} />
            <Field label="Comandă" value={p.order_ref} />
            <Field label="Ofertă" value={p.offer_ref} />
            <Field label="Status" value={PROJECT_STATUS[p.status]} />
          </div>
        </TabsContent>

        <TabsContent value="tehnic" className="mt-4">
          <div className="grid grid-cols-2 gap-5 rounded-xl border border-border bg-card p-6">
            <Field label="Dimensiuni" value={p.tech?.dimensions} />
            <Field label="Capacitate" value={p.tech?.capacity} />
            <Field label="Materiale" value={p.tech?.materials} />
            <Field label="Standarde" value={p.tech?.standards} />
            <div className="col-span-2"><Field label="Note tehnice" value={p.tech?.notes} /></div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border bg-card p-6" data-testid="tech-revision-panel">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Revizie proiect tehnic</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="font-head text-2xl font-extrabold tabular-nums">Rev. {p.tech_revision || 0}</span>
                {(p.tech_revision || 0) >= 3 && <span className="rounded bg-red-500/15 px-2 py-0.5 text-xs font-bold text-red-400">Escaladat la Director Executiv</span>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">La Revizia 3 se notifică automat Directorul Executiv (regula R02).</p>
            </div>
            {hasRole("inginer", "pm", "admin") && (
              <Button variant="outline" onClick={bumpRevision} data-testid="bump-revision-btn">
                <ArrowsClockwise size={16} className="mr-1.5" /> Revizie nouă
              </Button>
            )}
          </div>
        </TabsContent>

        <TabsContent value="termene" className="mt-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Termen contractual</div>
              <div className="mt-2 font-head text-xl font-bold">{fmtDate(p.contract_deadline)}</div>
            </div>
            <div className="rounded-xl border border-primary/30 bg-card p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-primary">Țintă internă</div>
              <div className="mt-2 font-head text-xl font-bold">{fmtDate(p.internal_target)}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Estimat curent</div>
              <div className="mt-2 font-head text-xl font-bold">{fmtDate(p.current_estimate)}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-5 rounded-xl border border-border bg-card p-6">
            <Field label="Condiții livrare" value={p.delivery_terms} />
            <Field label="Condiții plată" value={p.payment_terms} />
          </div>

          <div className="mt-4 rounded-xl border border-primary/30 bg-card p-6" data-testid="momente-panel">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-head text-lg font-bold">Momente cheie</h3>
              {canMontaj && (
                <Button variant="outline" size="sm" onClick={runMontajReminders} data-testid="run-montaj-reminders-btn">
                  Trimite remindere montaj acum
                </Button>
              )}
            </div>
            <p className="mb-4 text-xs text-muted-foreground">
              Setează datele-cheie. Operațiunea de workflow potrivită se leagă automat de moment, iar montajul are reminder cu 3 zile înainte (PM + directori).
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {(momente?.momente || [
                { label: "Livrare", field: "data_livrare", date: (p.data_livrare || "").slice(0, 10), linked_operation: "" },
                { label: "Montaj", field: "data_montaj", date: (p.data_montaj || "").slice(0, 10), linked_operation: "" },
              ]).map((m) => (
                <div key={m.field} className="rounded-lg border border-border p-4" data-testid={`moment-${m.field}`}>
                  <div className="text-xs font-bold uppercase tracking-wider text-primary">{m.label}</div>
                  {canEdit ? (
                    <Input type="date" className="mt-2" value={(m.date || "").slice(0, 10)}
                      onChange={(e) => setMoment(m.field, e.target.value)} data-testid={`moment-input-${m.field}`} />
                  ) : (
                    <div className="mt-2 font-head text-xl font-bold">{fmtDate(m.date)}</div>
                  )}
                  {m.linked_operation && (
                    <div className="mt-2 text-[11px] text-muted-foreground">
                      Operațiune legată: <span className="text-foreground/90">{m.linked_operation}</span>
                      {m.operation_status && m.operation_status !== "pending" && ` (${m.operation_status})`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="echipa" className="mt-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {[["Echipă", p.team], ["Vehicule / Scule", p.vehicles], ["Subcontractori", p.subcontractors]].map(([lbl, arr]) => (
              <div key={lbl} className="rounded-xl border border-border bg-card p-6">
                <div className="mb-3 text-xs font-bold uppercase tracking-wider text-muted-foreground">{lbl}</div>
                <div className="space-y-1.5">
                  {(arr || []).length ? arr.map((x, i) => (
                    <div key={i} className="rounded-lg bg-white/5 px-3 py-2 text-sm">{x}</div>
                  )) : <p className="text-sm text-muted-foreground">Nealocat.</p>}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {canMontaj && (
          <TabsContent value="echipe_montaj" className="mt-4">
            <MontajTeamsPanel project={p} reload={load} />
          </TabsContent>
        )}

        <TabsContent value="buget" className="mt-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Buget</div>
              <div className="mt-2 font-head text-2xl font-bold tabular-nums">{fmtMoney(p.budget)}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Costuri</div>
              <div className="mt-2 font-head text-2xl font-bold tabular-nums">{fmtMoney(p.costs)}</div>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Marjă</div>
              <div className={cn("mt-2 font-head text-2xl font-bold tabular-nums", margin >= 20 ? "text-emerald-400" : margin >= 0 ? "text-amber-400" : "text-red-400")}>
                {margin}%
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documente" className="mt-4">
          <DocumentsPanel projectId={id} />
        </TabsContent>

        <TabsContent value="dosar" className="mt-4">
          <ProjectFolderPanel projectId={id} positions={p.positions || []} highlightDoc={folderHint} />
        </TabsContent>

        <TabsContent value="workflow" className="mt-4">
          <WorkflowPanel projectId={id} highlightHint={wfHint} />
        </TabsContent>

        <TabsContent value="taskuri" className="mt-4">
          <TasksPanel projectId={id} />
        </TabsContent>

        <TabsContent value="planificare" className="mt-4">
          <PlanningPanel projectId={id} />
        </TabsContent>

        <TabsContent value="productie" className="mt-4">
          <ProductionPanel projectId={id} />
        </TabsContent>

        <TabsContent value="repere" className="mt-4">
          <ReperePanel projectId={id} />
        </TabsContent>

        <TabsContent value="scule" className="mt-4">
          <ProjectToolsPanel projectId={id} />
        </TabsContent>

        <TabsContent value="rezervoare" className="mt-4">
          <ProjectPositionsPanel project={p} reload={load} />
        </TabsContent>

        <TabsContent value="memorie_ai" className="mt-4">
          <ProjectMemory project={p} reload={load} />
        </TabsContent>

        {canMontaj && (
          <TabsContent value="finalizare_montaj" className="mt-4">
            <MontajFinalizare project={p} reload={load} />
          </TabsContent>
        )}

        <TabsContent value="teren" className="mt-4">
          <FieldPanel projectId={id} />
        </TabsContent>

        <TabsContent value="garantie" className="mt-4">
          <WarrantyPanel project={p} reload={load} />
        </TabsContent>

        <TabsContent value="riscuri" className="mt-4">
          <div className="rounded-xl border border-border bg-card p-6">
            {canEdit && (
              <div className="mb-4 flex flex-wrap gap-2">
                <Input placeholder="Titlu risc…" value={risk.title} onChange={(e) => setRisk({ ...risk, title: e.target.value })} className="flex-1 min-w-[160px]" data-testid="risk-title" />
                <Select value={risk.level} onValueChange={(v) => setRisk({ ...risk, level: v })}>
                  <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(RISK).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
                <Button onClick={addRisk} data-testid="add-risk-btn"><Plus size={16} /></Button>
              </div>
            )}
            <div className="space-y-2">
              {(p.risks || []).map((r) => (
                <div key={r.id} className="flex items-start gap-3 rounded-lg border border-border px-3 py-2.5">
                  <RiskDot level={r.level} className="mt-1" />
                  <div className="flex-1">
                    <div className="text-sm font-medium">{r.title}</div>
                    {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                  </div>
                  {canEdit && <button onClick={() => delRisk(r.id)} className="text-muted-foreground hover:text-red-400"><Trash size={15} /></button>}
                </div>
              ))}
              {(p.risks || []).length === 0 && <p className="text-sm text-muted-foreground">Niciun risc înregistrat.</p>}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="max-w-2xl" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle>Editează Fișa Unică</DialogTitle></DialogHeader>
          {form && (
            <div className="grid max-h-[65vh] gap-3 overflow-y-auto pr-1">
              {hasRole("admin") && (
                <div className="grid gap-1.5"><Label>Cod proiect</Label><Input value={form.code || ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="ex: HGS 1904" data-testid="edit-project-code" /><p className="text-[11px] text-muted-foreground">Doar adminul poate redenumi codul (ex: HGS-2026-002 → HGS 1904).</p></div>
              )}
              <div className="grid gap-1.5"><Label>Nume</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label>Client</Label><Input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Beneficiar</Label><Input value={form.beneficiary} onChange={(e) => setForm({ ...form, beneficiary: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Țară</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Adresă șantier</Label><Input value={form.site_address} onChange={(e) => setForm({ ...form, site_address: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Tip produs</Label><Input value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })} /></div>
                <div className="grid gap-1.5">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PROJECT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-1.5"><Label>Contract</Label><Input value={form.contract_ref} onChange={(e) => setForm({ ...form, contract_ref: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Comandă</Label><Input value={form.order_ref} onChange={(e) => setForm({ ...form, order_ref: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Ofertă</Label><Input value={form.offer_ref} onChange={(e) => setForm({ ...form, offer_ref: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="grid gap-1.5"><Label>Termen contractual</Label><Input type="date" value={(form.contract_deadline || "").slice(0, 10)} onChange={(e) => setForm({ ...form, contract_deadline: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Țintă internă</Label><Input type="date" value={(form.internal_target || "").slice(0, 10)} onChange={(e) => setForm({ ...form, internal_target: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Estimat curent</Label><Input type="date" value={(form.current_estimate || "").slice(0, 10)} onChange={(e) => setForm({ ...form, current_estimate: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label>Dată livrare</Label><Input type="date" value={(form.data_livrare || "").slice(0, 10)} onChange={(e) => setForm({ ...form, data_livrare: e.target.value })} data-testid="edit-data-livrare" /></div>
                <div className="grid gap-1.5"><Label>Dată montaj</Label><Input type="date" value={(form.data_montaj || "").slice(0, 10)} onChange={(e) => setForm({ ...form, data_montaj: e.target.value })} data-testid="edit-data-montaj" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-1.5"><Label>Buget (€)</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} /></div>
                <div className="grid gap-1.5"><Label>Costuri (€)</Label><Input type="number" value={form.costs} onChange={(e) => setForm({ ...form, costs: e.target.value })} /></div>
              </div>
              <div className="grid gap-1.5"><Label>Condiții livrare</Label><Input value={form.delivery_terms} onChange={(e) => setForm({ ...form, delivery_terms: e.target.value })} /></div>
              <div className="grid gap-1.5"><Label>Condiții plată</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></div>
              <div className="rounded-lg border border-border p-3" data-testid="fp-section">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Fișă tehnică unică (FP) — 1:1 cu Excelul</div>
                  <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-primary/40 bg-primary/[0.06] px-2.5 py-1.5 text-xs text-primary hover:bg-primary/10" data-testid="fp-excel-upload">
                    <input type="file" accept=".xls,.xlsx" className="hidden" onChange={onFpExcel} disabled={fpBusy} />
                    <FileText size={14} /> {fpBusy ? "Se importă…" : "Încarcă Excel fișă tehnică"}
                  </label>
                </div>
                {fpSchema.map((sec) => (
                  <div key={sec.title} className="mb-3">
                    <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground/80">{sec.title}</div>
                    {sec.fields.some((f) => f.key === "racorduri") ? (
                      <div className="grid gap-1.5">
                        {(form.fp?.racorduri || []).map((r, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <Input className="flex-1" placeholder="Tip racord" value={r.tip || ""} onChange={(e) => setRacord(i, "tip", e.target.value)} />
                            <Input className="w-24" placeholder="Nr. buc" value={r.nr_buc || ""} onChange={(e) => setRacord(i, "nr_buc", e.target.value)} />
                            <Input className="w-24" placeholder="DN" value={r.dn || ""} onChange={(e) => setRacord(i, "dn", e.target.value)} />
                            <button type="button" onClick={() => delRacord(i)} className="text-muted-foreground hover:text-red-400"><Trash size={14} /></button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={addRacord} className="w-fit"><Plus size={14} className="mr-1" /> Adaugă racord</Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-3">
                        {sec.fields.map((f) => (
                          <div key={f.key} className="grid gap-1"><Label className="text-xs">{f.label}</Label>
                            <Input value={form.fp?.[f.key] || ""} onChange={(e) => setFp(f.key, e.target.value)} data-testid={`fp-${f.key}`} /></div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="rounded-lg border border-border p-3">
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Cerințe tehnice</div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Dimensiuni" value={form.tech?.dimensions || ""} onChange={(e) => setForm({ ...form, tech: { ...form.tech, dimensions: e.target.value } })} />
                  <Input placeholder="Capacitate" value={form.tech?.capacity || ""} onChange={(e) => setForm({ ...form, tech: { ...form.tech, capacity: e.target.value } })} />
                  <Input placeholder="Materiale" value={form.tech?.materials || ""} onChange={(e) => setForm({ ...form, tech: { ...form.tech, materials: e.target.value } })} />
                  <Input placeholder="Standarde (EN12845…)" value={form.tech?.standards || ""} onChange={(e) => setForm({ ...form, tech: { ...form.tech, standards: e.target.value } })} />
                </div>
                <Textarea className="mt-3" placeholder="Note tehnice" value={form.tech?.notes || ""} onChange={(e) => setForm({ ...form, tech: { ...form.tech, notes: e.target.value } })} />
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="save-project-edit">Salvează</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
